import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import find from 'lodash/find';
import get from 'lodash/get';
import React, { useCallback, useContext } from 'react';
import set from 'lodash/set';
import styled from 'styled-components';

import { LinkToolbarButton, PlateProvider } from '@udecode/plate';
import FormContext from 'src/presentation/contexts/FormContext';
import { TextEditor } from 'src/presentation/components/agent_desktop_components';
import { useDebounced } from '@ad-scripts/presentation/components/hooks/debounce_hooks';
import { serializeHtml } from '@ad-scripts/presentation/components/text_editor_new/lib/serialize_html';
import { editorHasNoText } from '@ad-scripts/presentation/components/text_editor_new/lib/editor_has_no_text';
import getSelectedPlaintext from '@ad-scripts/presentation/components/text_editor_new/lib/get_selected_plaintext';
import useInitialValue from '@ad-scripts/presentation/components/text_editor_new/hooks/use_initial_value';
import useEditorRef, { linkPlugin } from './useEditorRef';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import { InputSize } from 'src/presentation/components/forms/InputSize';

const HtmlEditor = ({
  editorPath,
  placeholder,
  label = '',
  description = '',
}: {
  editorPath: string;
  placeholder: string;
  label?: string | JSX.Element;
  description?: string | JSX.Element;
}) => {
  const editorRef = useEditorRef(editorPath, [linkPlugin]);
  const { errors, workingState, updateState } = useContext(FormContext);
  const err = find(errors, error => error.attr === editorPath);
  const editorBody = get(workingState, editorPath, false);
  const body = replaceCustomerName(editorBody, true);
  const initialValue = useInitialValue(body || '', editorRef.current);

  const onEditorChange = useCallback(() => {
    let message: string;
    if (editorHasNoText(editorRef.current)) {
      message = getSelectedPlaintext(editorRef.current);
    } else {
      const htmlMessage = serializeHtml(editorRef.current);
      message = replaceCustomerName(htmlMessage, false);
    }
    const changes = set({}, editorPath, message);
    updateState(changes, { replaceArrayValues: true });
  }, [editorRef, updateState, editorPath]);

  const debouncedOnChange = useDebounced(onEditorChange, 500, {});

  return (
    <FieldContainer
      error={err ? err.detail : undefined}
      label={label}
      data-aid={editorPath}
      description={description}
      control={
        <StyledHtmlEditor>
          <PlateProvider
            id={editorPath}
            editor={editorRef.current}
            initialValue={initialValue}
            onChange={debouncedOnChange}
            plugins={editorRef.current.plugins}
          >
            <StyledToolbar>
              <LinkToolbarButton id={editorPath} icon={<FontAwesomeIcon icon={faLink} />} />
            </StyledToolbar>

            <StyledTextEditorWrapper>
              <TextEditor editorId={editorPath} data-aid={editorPath} editorRef={editorRef} placeholder={placeholder} />
            </StyledTextEditorWrapper>
          </PlateProvider>
        </StyledHtmlEditor>
      }
    />
  );
};

// CUSTOMERNAME is a variable that is replaced with the customer's name. It requires special encoding/decoding with the HTML editor.
// When rendering, <CUSTOMERNAME> is replaced with the HTML encoded string to show the customer name variable.
// When saving, the HTML encoded string is replaced with <CUSTOMERNAME> to save the customer name variable.
export const replaceCustomerName = (body, toDisplay) => {
  if (!body) {
    return body;
  }
  if (toDisplay) {
    return body.replace('<CUSTOMERNAME>', '&lt;CUSTOMERNAME&gt;');
  }
  return body.replace('&lt;CUSTOMERNAME&gt;', '<CUSTOMERNAME>');
};

const StyledToolbar = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.gray400};
  display: flex;
  gap: 8px;
  align-items: center;
  height: 35px;
`;

const StyledTextEditorWrapper = styled.div`
  height: 108px;
  overflow-y: auto;
`;

const StyledHtmlEditor = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => (p.hasError ? p.theme.colors.red400 : p.theme.colors.gray400)};
  border-radius: 4px;
  margin-top: 8px;
  margin-bottom: 8px;
  position: relative;
  width: ${InputSize.XLARGE}px;

  & [data-slate-editor='true'] {
    padding: 8px;
  }

  &:focus-within {
    border-color: ${p => p.theme.colors.green400};
  }

  & .slate-ToolbarButton-active,
  & .slate-ToolbarButton:hover {
    color: ${p => p.theme.colors.green400};
  }
`;

export default HtmlEditor;
